<script setup lang="ts">
import {LineChartWidget} from "@/laboratory/components/dashboard/dashboard";
import {Line as LineChartComponent} from "vue-chartjs";
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LinearScale,
    CategoryScale, Filler, BarElement, ArcElement
} from "chart.js";
import type {ChartOptions} from "chart.js";
import {Widget} from "@/laboratory/components/dashboard/components/widget";
import {computed} from "vue";
import ChartDataLabels from "chartjs-plugin-datalabels";
import ChartService from "@/laboratory/services/ChartService";

ChartJS.register(Title, Tooltip, Legend, Filler, BarElement, CategoryScale, LinearScale, ArcElement);

const props = defineProps<{
    widget: LineChartWidget;
}>();

/**
 * The chart options.
 */
const chartOptions = computed<ChartOptions<'line'>>(() => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
            },
            datalabels: {
                color: "primary",
                anchor: "end",
                offset: -3,
                align: 'top',
                font: {
                    size: 8
                },
                formatter: (value: number) => {
                    return value;
                }
            },
        },
        scales: {
            y: {
                ticks: {
                    callback: (tickValue: string | number) => {
                        return tickValue;
                    },
                },
            },
            x: {
                ticks: {
                    autoSkip: false,
                    font: {
                        size: 9,
                    },
                },
            },
        },
        elements: {
            line: {
                tension: 0.3
            }
        },
    };
});

const chartData = computed(() => {
    return {
        labels: props.widget.data.labels,
        datasets: props.widget.data.datasets.map((dataset, index) => {
            return {
                label: dataset.label,
                data: dataset.values,
                borderColor: dataset.color ?? ChartService.getColor(index),
                backgroundColor: dataset.color ?? ChartService.getColor(index)
            };
        }),
    };
});
</script>

<template>
  <Widget :widget="widget">
    <LineChartComponent
      :data="chartData"
      :options="chartOptions"
      class="flex flex-1 h-96"
      :plugins="[ChartDataLabels as any]"
    />
  </Widget>
</template>
